import React, { useState, useEffect, useRef } from "react";
import { useLanguage } from "../context/LanguageContext";

function ProjectCard({ project, delay }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="project-card"
      style={{
        opacity: visible ? 1 : 0,
        transform: `translateY(${visible ? 0 : 30}px)`,
        transition: `all 0.6s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      }}
    >
      <div className="project-name">{project.name}</div>
      <div className="project-role">{project.role}</div>
      <div className="project-stack">
        {project.stack.map((s) => (
          <span key={s}>{s}</span>
        ))}
      </div>
      <ul className="project-tasks">
        {project.tasks.map((task, i) => (
          <li key={i}>{task}</li>
        ))}
      </ul>
    </div>
  );
}

function Projects() {
  const { t, lang } = useLanguage();
  const headerRef = useRef(null);
  const [headerVisible, setHeaderVisible] = useState(false);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setHeaderVisible(true); obs.disconnect(); } },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const projects = lang === "fr" ? [
    {
      name: "Clyx",
      role: "Lead Developer & Développeur Full-Stack",
      stack: ["React", "Express", "PostgreSQL", "Salesforce Marketing Cloud"],
      tasks: [
        "Conception et développement d'une Custom Activity d'envoi de SMS intégrée à Salesforce Marketing Cloud.",
        "Mise en place d'une base de données distribuée pour la scalabilité et le suivi de délivrabilité.",
        "Gestion complète des déploiements et de l'infrastructure.",
        "Solution déployée en production et utilisée par Air Côte d'Ivoire.",
      ],
    },
    {
      name: "Lycs Agentforce",
      role: "Concepteur & Développeur IA",
      stack: ["Salesforce Agentforce", "WhatsApp Business API", "Slack"],
      tasks: [
        "Mise en place d'un chatbot Salesforce Agentforce intégré à WhatsApp.",
        "Création, listing et conversion de leads et contacts par conversation.",
        "Automatisation du cycle de gestion des leads depuis WhatsApp.",
      ],
    },
    {
      name: "Portail Vierspiren",
      role: "Développeur Salesforce & MuleSoft",
      stack: ["Salesforce (Apex, LWC)", "MuleSoft"],
      tasks: [
        "Conception, design et développement du portail.",
        "Flux MuleSoft, composants Salesforce, intégrations.",
        "Déploiement et mise en production en environnement client.",
      ],
    },
    {
      name: "Air Côte d'Ivoire",
      role: "Développeur MuleSoft & Marketing Cloud",
      stack: ["MuleSoft", "Salesforce Marketing Cloud"],
      tasks: [
        "Développement de flux d'intégration MuleSoft.",
        "Développement de templates email et CloudPages sur Marketing Cloud.",
      ],
    },
    {
      name: "Renault",
      role: "Développeur Marketing Cloud",
      stack: ["Salesforce Marketing Cloud", "HTML/CSS", "AMPscript"],
      tasks: [
        "Développement de templates email et CloudPages pour les campagnes du groupe.",
      ],
    },
    {
      name: "BIC Excellence Awards",
      role: "Développeur Full-Stack",
      stack: ["Spring Boot", "Java/J2EE"],
      tasks: [
        "Développement back-end et front-end d'une plateforme événementielle pour BIC.",
      ],
    },
    {
      name: "Lycs Fid",
      role: "Développeur Mobile",
      stack: ["Flutter", "Dart"],
      tasks: [
        "Développement d'une application mobile de fidélisation client multiplateforme (iOS & Android).",
      ],
    },
    {
      name: "Lycs Satisfaction",
      role: "Développeur Mobile",
      stack: ["Flutter", "Dart"],
      tasks: [
        "Développement d'une application mobile de gestion des enquêtes de satisfaction client.",
      ],
    },
  ] : [
    {
      name: "Clyx",
      role: "Lead Developer & Full-Stack Developer",
      stack: ["React", "Express", "PostgreSQL", "Salesforce Marketing Cloud"],
      tasks: [
        "Design and development of a Custom Activity for SMS sending integrated into Salesforce Marketing Cloud.",
        "Implementation of a distributed database for scalability and deliverability tracking.",
        "Full management of deployments and infrastructure.",
        "Solution deployed in production and used by Air Côte d'Ivoire.",
      ],
    },
    {
      name: "Lycs Agentforce",
      role: "AI Designer & Developer",
      stack: ["Salesforce Agentforce", "WhatsApp Business API", "Slack"],
      tasks: [
        "Implementation of a Salesforce Agentforce chatbot integrated with WhatsApp.",
        "Lead creation, listing and conversion through conversation.",
        "Lead management cycle automation from WhatsApp.",
      ],
    },
    {
      name: "Vierspiren Portal",
      role: "Salesforce & MuleSoft Developer",
      stack: ["Salesforce (Apex, LWC)", "MuleSoft"],
      tasks: [
        "Portal design, development and implementation.",
        "MuleSoft flows, Salesforce components, integrations.",
        "Deployment and production in client environment.",
      ],
    },
    {
      name: "Air Côte d'Ivoire",
      role: "MuleSoft & Marketing Cloud Developer",
      stack: ["MuleSoft", "Salesforce Marketing Cloud"],
      tasks: [
        "Development of MuleSoft integration flows.",
        "Email templates and CloudPages development on Marketing Cloud.",
      ],
    },
    {
      name: "Renault",
      role: "Marketing Cloud Developer",
      stack: ["Salesforce Marketing Cloud", "HTML/CSS", "AMPscript"],
      tasks: [
        "Email templates and CloudPages development for group campaigns.",
      ],
    },
    {
      name: "BIC Excellence Awards",
      role: "Full-Stack Developer",
      stack: ["Spring Boot", "Java/J2EE"],
      tasks: [
        "Back-end and front-end development of an event platform for BIC.",
      ],
    },
    {
      name: "Lycs Fid",
      role: "Mobile Developer",
      stack: ["Flutter", "Dart"],
      tasks: [
        "Development of a cross-platform customer loyalty mobile app (iOS & Android).",
      ],
    },
    {
      name: "Lycs Satisfaction",
      role: "Mobile Developer",
      stack: ["Flutter", "Dart"],
      tasks: [
        "Development of a customer satisfaction survey mobile app.",
      ],
    },
  ];

  return (
    <section className="projects-section" id="projects">
      <div className="projects-content">
        <div
          ref={headerRef}
          style={{
            textAlign: "center",
            marginBottom: 16,
            opacity: headerVisible ? 1 : 0,
            transform: `translateY(${headerVisible ? 0 : -20}px)`,
            transition: "all 0.8s cubic-bezier(0.16,1,0.3,1)",
          }}
        >
          <p className="section-eyebrow">{t("projEyebrow")}</p>
          <h2 className="section-title">{t("projTitle")}<span className="dot">.</span></h2>
          <p className="section-subtitle">{t("projSubtitle")}</p>
          <div className="section-underline" style={{ width: headerVisible ? 80 : 0, margin: "14px auto 0" }} />
        </div>

        <div className="projects-grid">
          {projects.map((proj, i) => (
            <ProjectCard key={proj.name} project={proj} delay={i * 70} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default Projects;
